/**
 * CCTV 관계 편집 패널 — offline-manage 콘솔의 "CCTV 관계도" 를 맵 에디터 안으로
 * 옮긴 것. 조명서버의 순차 점등이 이 관계를 따라 번지므로, 여기가 틀리면 점등이
 * 엉뚱한 방향으로 흐른다.
 *
 * 이 관계는 현장 router DB(cctv_connections)에 사는 값이고 본사 manage 백엔드는
 * 현장 DB 에 닿지 않는다. 그래서 여기서는 맵 좌표로 만들고 화면에서 고친 뒤
 * 파일로 내보내고, DB 반영은 현장 콘솔의 "관계 올리기" 가 한다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import type { dia } from '@joint/core'

import { isCctvElement } from '../../editor/lib/zoneCctvRelations'
import { getActiveCanvasRenderer } from '../../viewer/canvas/activeRenderer'
import { getActiveOverlayCanvas } from '../../../map-editor-pages/editor/hooks/useCanvasEditorOverlay'
import { cctvName, collectRelationCameras, collectRelationObstacles } from '../lib/graphAdapter'
import { generateRelations, pairRows, rowsFor } from '../lib/generateRelations'
import {
  cctvAt,
  clearRelationLinks,
  drawRelationLinks,
  RELATION_LINK_FLAG,
  relationPairAt,
} from '../lib/relationOverlay'
import { dedupe, parseRelationJson, toRelationJson, toRelationSql } from '../lib/serialize'
import type { CctvConnection, CctvRelationDirection, RelationCamera } from '../model/types'

interface Props {
  graph: dia.Graph | null
  /** 맵 클릭(관계선 빼기·CCTV 두 대 잇기)을 받는 paper. 없으면 패널로만 편집한다. */
  paper?: dia.Paper | null
  /** 관계선 굵기 — 맵 좌표계 배율에 맞춘 값(주차면 기준). */
  strokeWidth: number
  onClose: () => void
}

/** 이 거리(px) 미만으로 움직였으면 팬이 아니라 클릭. 에디터 오버레이와 같은 기준. */
const CLICK_MOVE_PX = 4
/** CCTV·관계선 클릭 허용 거리(화면 px). */
const LINE_HIT_PX = 8

/** a↔b 사이 행을 방향과 무관하게 모두 뺀다 — 한쪽만 남으면 점등이 한 방향으로만 번진다. */
function withoutPair(rows: CctvConnection[], a: string, b: string): CctvConnection[] {
  return rows.filter((r) => !((r.name === a && r.to === b) || (r.name === b && r.to === a)))
}

function download(name: string, body: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([body], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

export const CctvRelationPanel: React.FC<Props> = ({ graph, paper, strokeWidth, onClose }) => {
  const cameras = useMemo(() => collectRelationCameras(graph), [graph])
  const names = useMemo(
    () => cameras.map((c) => c.name).sort((a, b) => a.localeCompare(b)),
    [cameras],
  )

  const [rows, setRows] = useState<CctvConnection[]>([])
  const [selected, setSelected] = useState('')
  const [addTo, setAddTo] = useState('')
  const [addDir, setAddDir] = useState<CctvRelationDirection>('left')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  // 맵에서 잇기의 첫 번째로 클릭한 CCTV — 두 번째 클릭에서 쌍이 된다.
  const [pickFrom, setPickFrom] = useState<RelationCamera | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const redraw = useCallback(
    (next: CctvConnection[], only: string) => {
      // 기준 CCTV 의 행을 앞에 둔다 — 캔버스는 쌍마다 먼저 나온 행의 색으로 그리므로
      // 기준 CCTV 에서 본 왼쪽(파랑)/오른쪽(초록)이 된다.
      const target = only
        ? next
            .filter((r) => r.name === only || r.to === only)
            .sort((a, b) => Number(b.name === only) - Number(a.name === only))
        : next
      const { drawn, skipped } = drawRelationLinks(graph, target, strokeWidth)
      setNote(
        skipped > 0
          ? `관계선 ${drawn}개 표시 · 맵에 없는 카메라 ${skipped}행은 건너뜀`
          : `관계선 ${drawn}개 표시`,
      )
    },
    [graph, strokeWidth],
  )

  const apply = useCallback(
    (next: CctvConnection[], only = selected) => {
      const clean = dedupe(next)
      setRows(clean)
      redraw(clean, only)
      return clean
    },
    [redraw, selected],
  )

  const handleGenerate = useCallback(() => {
    setError('')
    if (cameras.length < 2) {
      setError('이름이 있는 CCTV 가 2대 미만입니다 — 먼저 CCTV 이름을 매핑하세요.')
      return
    }
    const result = generateRelations(cameras, collectRelationObstacles(graph))
    apply(result.rows, '')
    setNote(
      `자동 생성 ${result.rows.length}행` +
        (result.lonely > 0 ? ` · 이웃을 못 찾은 카메라 ${result.lonely}대` : ''),
    )
  }, [apply, cameras, graph])

  const handleSelect = useCallback(
    (name: string) => {
      setSelected(name)
      setAddTo('')
      redraw(rows, name)
    },
    [redraw, rows],
  )

  const handleAdd = useCallback(() => {
    setError('')
    if (!selected || !addTo) {
      setError('기준 CCTV 와 이웃 CCTV 를 모두 고르세요.')
      return
    }
    // 조명서버는 양방향 행을 각각 읽는다 — 반대편 행도 같이 넣어야 점등이
    // 되돌아온다(자동 생성도 쌍으로 만든다).
    const back: CctvRelationDirection = addDir === 'left' ? 'right' : 'left'
    apply([
      ...rows,
      { name: selected, to: addTo, direction: addDir, depth: 1 },
      { name: addTo, to: selected, direction: back, depth: 1 },
    ])
    setAddTo('')
  }, [addDir, addTo, apply, rows, selected])

  const handleDelete = useCallback(
    (row: CctvConnection) => apply(withoutPair(rows, row.name, row.to)),
    [apply, rows],
  )

  // 맵 클릭 편집 — offline-manage 관계도 미리보기와 같은 조작. 관계선 클릭 = 그 쌍
  // 빼기, CCTV 두 대를 차례로 클릭 = 잇기(좌우는 자동 생성과 같은 좌표 규칙).
  // 맵에 그어진 선이 곧 관계 전부다 — 선이 없는 카메라끼리는 관계가 없다.
  const dropPair = useCallback(
    (a: string, b: string) => {
      apply(withoutPair(rows, a, b))
      setNote(`${a} ↔ ${b} 연결을 뺐습니다`)
    },
    [apply, rows],
  )

  const pickCctv = useCallback(
    (element: dia.Element) => {
      const name = cctvName(element)
      if (!name) {
        setError('이름이 없는 CCTV 는 이을 수 없습니다 — 먼저 CCTV 이름을 매핑하세요.')
        return
      }
      setError('')
      const center = element.getBBox().center()
      const here: RelationCamera = { name, x: center.x, y: center.y }
      if (!pickFrom) {
        setPickFrom(here)
        setNote(`${name} 에서 이을 CCTV 를 클릭하세요 (취소: 같은 CCTV 또는 Esc)`)
        return
      }
      setPickFrom(null)
      if (pickFrom.name === name) {
        setNote('잇기를 취소했습니다')
        return
      }
      if (withoutPair(rows, pickFrom.name, name).length !== rows.length) {
        setError(`${pickFrom.name} ↔ ${name} 은(는) 이미 연결돼 있습니다.`)
        return
      }
      apply([...rows, ...pairRows(pickFrom, here)])
      setNote(`${pickFrom.name} ↔ ${name} 연결을 추가했습니다`)
    },
    [apply, pickFrom, rows],
  )

  // Canvas 에디터(기본) — paper 가 얼어 있어 셀 이벤트가 없다. 오버레이 캔버스에서
  // 클릭(이동 4px 미만, 팬과 구분)을 직접 받아 CCTV·선 모두 거리로 찾는다.
  useEffect(() => {
    const canvas = getActiveOverlayCanvas()
    const renderer = getActiveCanvasRenderer()
    if (!canvas || !renderer || !graph) return
    let down: { x: number; y: number } | null = null
    const onDown = (e: MouseEvent) => {
      down = { x: e.clientX, y: e.clientY }
    }
    const onUp = (e: MouseEvent) => {
      const start = down
      down = null
      if (!start || Math.hypot(e.clientX - start.x, e.clientY - start.y) >= CLICK_MOVE_PX) return
      const rect = canvas.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      const p = renderer.screenToWorld(sx, sy)
      const tol = Math.abs(renderer.screenToWorld(sx + LINE_HIT_PX, sy).x - p.x)
      const cctv = cctvAt(graph, p, tol)
      if (cctv) {
        pickCctv(cctv)
        return
      }
      const pair = relationPairAt(graph, rows, p, tol)
      if (pair) dropPair(pair[0], pair[1])
    }
    canvas.addEventListener('mousedown', onDown)
    canvas.addEventListener('mouseup', onUp)
    return () => {
      canvas.removeEventListener('mousedown', onDown)
      canvas.removeEventListener('mouseup', onUp)
    }
  }, [dropPair, graph, pickCctv, rows])

  // SVG 에디터(CANVAS_EDITOR 꺼짐) — 관계선이 링크 셀이라 paper 이벤트로 받는다.
  // hybridRenderer 는 캔버스로 그린 셀에 가짜 cellView({ model: { id } })를
  // 넘기므로 id 로 실제 셀을 다시 찾는다.
  useEffect(() => {
    if (!paper || !graph || getActiveCanvasRenderer()) return
    const onClick = (view: { model?: { id?: dia.Cell.ID } }) => {
      const id = view?.model?.id
      const cell = id == null ? undefined : graph.getCell(id)
      if (!cell) return
      if (cell.isLink()) {
        const data = (cell.get('data') as Record<string, unknown>) || {}
        if (data[RELATION_LINK_FLAG] === true) dropPair(String(data.name), String(data.to))
        return
      }
      if (cell.isElement() && isCctvElement(cell)) pickCctv(cell)
    }
    paper.on('cell:pointerclick', onClick)
    return () => {
      paper.off('cell:pointerclick', onClick)
    }
  }, [dropPair, graph, paper, pickCctv])

  useEffect(() => {
    if (!pickFrom) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setPickFrom(null)
      setNote('잇기를 취소했습니다')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [pickFrom])

  const handleImport = useCallback(
    async (file: File) => {
      setError('')
      try {
        const parsed = parseRelationJson(await file.text())
        apply(parsed, '')
        setNote(`파일에서 ${parsed.length}행 불러옴`)
      } catch (e) {
        setError(`관계 파일을 읽지 못했습니다: ${(e as Error).message}`)
      }
    },
    [apply],
  )

  const handleClose = useCallback(() => {
    clearRelationLinks(graph)
    onClose()
  }, [graph, onClose])

  const selectedRows = useMemo(() => rowsFor(rows, selected), [rows, selected])

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 380,
        maxHeight: 'calc(100% - 32px)',
        overflowY: 'auto',
        p: 2,
        zIndex: 20,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>
          CCTV 관계
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="닫기">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        조명 순차 점등이 따라가는 좌/우 이웃 체인입니다. 맵 좌표로 만들고 고친 뒤 파일로 내보내
        현장 콘솔에서 반영하세요.
      </Typography>
      {paper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          맵에서 관계선 클릭 = 빼기 · CCTV 두 대를 차례로 클릭 = 잇기(좌/우는 좌표로 자동).
          선으로 이어진 카메라끼리만 관계가 됩니다.
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Chip size="small" label={`CCTV ${names.length}대`} />
        <Chip
          size="small"
          label={`관계 ${rows.length}행`}
          color={rows.length ? 'primary' : 'default'}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" onClick={handleGenerate}>
          맵 좌표로 자동 생성
        </Button>
        <Button
          size="small"
          disabled={rows.length === 0}
          onClick={() => download('cctv-relations.json', toRelationJson(rows), 'application/json')}
        >
          JSON 내보내기
        </Button>
        <Button
          size="small"
          disabled={rows.length === 0}
          onClick={() => download('cctv-relations.sql', toRelationSql(rows), 'text/plain')}
        >
          SQL 내보내기
        </Button>
        <Button size="small" onClick={() => fileRef.current?.click()}>
          JSON 가져오기
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = '' // 같은 파일을 다시 골라도 change 가 오도록
            if (file) void handleImport(file)
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {!error && note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {note}
        </Typography>
      )}

      <Divider sx={{ my: 1.5 }} />

      <TextField
        select
        size="small"
        fullWidth
        label="기준 CCTV"
        value={selected}
        onChange={(e) => handleSelect(e.target.value)}
        helperText={
          selected ? '이 카메라와 이어진 관계선만 표시합니다' : '전체 관계선을 표시합니다'
        }
      >
        <MenuItem value="">(전체)</MenuItem>
        {names.map((n) => (
          <MenuItem key={n} value={n}>
            {n}
          </MenuItem>
        ))}
      </TextField>

      {selected && (
        <>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <TextField
              select
              size="small"
              label="방향"
              value={addDir}
              onChange={(e) => setAddDir(e.target.value as CctvRelationDirection)}
              sx={{ width: 110 }}
            >
              <MenuItem value="left">왼쪽</MenuItem>
              <MenuItem value="right">오른쪽</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="이웃 CCTV"
              value={addTo}
              onChange={(e) => setAddTo(e.target.value)}
              sx={{ flex: 1 }}
            >
              {names
                .filter((n) => n !== selected)
                .map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
            </TextField>
            <Button size="small" variant="outlined" onClick={handleAdd}>
              추가
            </Button>
          </Stack>

          <Box sx={{ mt: 1.5 }}>
            {selectedRows.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                이 카메라에 등록된 이웃이 없습니다.
              </Typography>
            ) : (
              selectedRows.map((row) => (
                <Stack
                  key={`${row.direction}-${row.to}`}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.25 }}
                >
                  <Chip
                    size="small"
                    label={row.direction === 'left' ? '왼쪽' : '오른쪽'}
                    color={row.direction === 'left' ? 'primary' : 'success'}
                    variant="outlined"
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {row.to}
                  </Typography>
                  <Tooltip title="이 관계를 양쪽 모두 삭제">
                    <IconButton size="small" onClick={() => handleDelete(row)} aria-label="삭제">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))
            )}
          </Box>
        </>
      )}
    </Paper>
  )
}
